import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageSquare } from "lucide-react";

const slides = [
  {
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
    title: "Professional Cleaning Services in Melbourne VIC",
    subtitle: "Residential & Commercial Cleaning",
    description: "Get sparkling clean homes & offices from trusted cleaning knights.",
    ctaText: "Book Now",
    link: "/contact"
  },
  {
    image: "https://images.unsplash.com/photo-1603712725038-e9334ae8f39f?q=80&w=2070&auto=format&fit=crop",
    title: "Emergency Cleaning Services in Melbourne VIC",
    subtitle: "24/7 Available",
    description: "Water damage, flood recovery, and disaster cleanup services available anytime.",
    ctaText: "Emergency Call",
    link: "/contact"
  },
  {
    image: "https://images.unsplash.com/photo-1527515637462-daf9b3d6c8d3?q=80&w=2070&auto=format&fit=crop",
    title: "End of Lease Cleaning in Melbourne VIC",
    subtitle: "Bond Back Guarantee",
    description: "Get your full bond back with our comprehensive end of lease cleaning service.",
    ctaText: "Book Bond Clean",
    link: "/services/end-of-lease-cleaning"
  },
  {
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
    title: "Carpet & Upholstery Cleaning in Melbourne VIC",
    subtitle: "Expert Fabric Care",
    description: "Professional cleaning for carpets, rugs, and furniture with stain removal guarantee.",
    ctaText: "View Service",
    link: "/services/carpet-upholstery-cleaning"
  }
];

const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);

  // WhatsApp phone number (using Australian format based on footer)
  const whatsappNumber = "+61420499661";
  const whatsappMessage = encodeURIComponent("Hello Knight Cleanings! I'm interested in your cleaning services. Could you please provide me with more information?");
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${whatsappMessage}`;

  // Autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Manual slide navigation
  const goToSlide = (index) => {
    setActiveSlide(index);
  };

  const goToNextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const goToPrevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative h-[600px] md:h-[700px] w-full overflow-hidden">
      {/* Background Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
            activeSlide === index ? "opacity-100 z-0" : "opacity-0 z-[-1]"
          }`}
          style={{ backgroundImage: `url(${slide.image})` }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-indigo-900/70 to-blue-900/80" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto flex flex-col items-center justify-center px-4 text-center text-white">
        <div className="space-y-6 max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-2">
            <span className="font-semibold text-amber-300">Trusted Since 2010</span>
          </div>
          
          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight animate-fade-in-up">
            {slides[activeSlide].title}
            <span className="block text-amber-300 mt-2">
              {slides[activeSlide].subtitle}
            </span>
          </h1>

          {/* Description */}
          <p className="mt-5 max-w-2xl mx-auto text-lg md:text-xl text-blue-100 animate-fade-in-up delay-200">
            {slides[activeSlide].description}
            <span className="block mt-2 text-white font-semibold">
              Affordable, fast & eco-friendly solutions across Melbourne.
            </span>
          </p>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-wrap gap-4 justify-center animate-fade-in-up delay-300">
            <Link
              to={slides[activeSlide].link}
              className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-2"
            >
              <span>{slides[activeSlide].ctaText}</span>
              <ArrowRight size={18} />
            </Link>
            
            <Link
              to="/services"
              className="border-2 border-white hover:bg-white hover:text-indigo-900 font-semibold px-8 py-3 rounded-full transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
            >
              <span>View All Services</span>
            </Link>
            
            <a
              href="tel:+61420499661"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center gap-2"
            >
              <Phone size={18} />
              <span>Call Now</span>
            </a>
          </div>

          {/* Service Highlights */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {[
              { text: "24/7 Emergency", icon: "🔄" },
              { text: "Bond Back Guarantee", icon: "🛡️" },
              { text: "Eco-Friendly", icon: "🌿" },
              { text: "Free Quotes", icon: "💰" }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm bg-white/5 backdrop-blur-sm px-3 py-2 rounded-lg">
                <span className="text-lg">{item.icon}</span>
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slider Navigation Buttons */}
      <button
        onClick={goToPrevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 rounded-full transition-all duration-300 z-20"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button
        onClick={goToNextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 rounded-full transition-all duration-300 z-20"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Slider Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
              activeSlide === index 
                ? "w-8 bg-amber-400" 
                : "w-2 bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-6 bottom-6 bg-green-500 hover:bg-green-600 p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-50 group"
        aria-label="Chat on WhatsApp"
      >
        <div className="relative">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
            alt="WhatsApp"
            className="h-8 w-8"
          />
          {/* Pulse Animation */}
          <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20" />
        </div>
        {/* Tooltip */}
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Chat with us on WhatsApp
        </div>
      </a>

      {/* Call Floating Button */}
      <a
        href="tel:+61420499661"
        className="fixed right-6 bottom-28 bg-blue-600 hover:bg-blue-700 p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-50 group"
        aria-label="Call us"
      >
        <div className="relative">
          <Phone size={24} className="text-white" />
          {/* Pulse Animation */}
          <div className="absolute inset-0 bg-blue-600 rounded-full animate-ping opacity-20" />
        </div>
        {/* Tooltip */}
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Call us: +61 420 499 661
        </div>
      </a>

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        .delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
        .delay-300 {
          animation-delay: 0.4s;
          opacity: 0;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;